import React, { type ReactNode } from 'react';
import {
  Badge,
  Button,
  Group,
  Modal,
  Paper,
  ScrollArea,
  SimpleGrid,
  Stack,
  Text,
  ThemeIcon,
} from '@mantine/core';
import {
  Building2,
  CircleUser,
  Folder,
  Gauge,
  Mail,
  MapPin,
  Phone,
  Truck,
  User,
} from 'lucide-react';

// A titled block of the modal.
const Section = ({ title, children }: { title: string; children: ReactNode }) => (
  <Paper withBorder p="md" radius="md">
    <Text fw={600} mb="sm">
      {title}
    </Text>
    {children}
  </Paper>
);

// An icon with a small label and its value.
const Field = ({
  icon,
  color,
  label,
  children,
}: {
  icon: ReactNode;
  color: string;
  label: string;
  children: ReactNode;
}) => (
  <Group gap="sm" align="flex-start" wrap="nowrap">
    <ThemeIcon variant="light" color={color} size="md" radius="md">
      {icon}
    </ThemeIcon>
    <Stack gap={0} miw={0}>
      <Text size="xs" c="dimmed" fw={500}>
        {label}
      </Text>
      <Text size="sm" component="div" style={{ wordBreak: 'break-word' }}>
        {children}
      </Text>
    </Stack>
  </Group>
);

const EmployeeInfoModal = ({
  employee,
  onClose,
}: {
  employee: {
    name: string;
    work_email: string;
    mobile_phone: string;
    status: boolean;
    company: { name: string };
    project: { name: string };
    territories: Array<{ id: number; name: string }>;
    warehouses: Array<{ id: number; name: string }>;
    vehicle?: { name: string };
    meter_reading?: string;
    manager?: {
      name: string;
      work_email?: string;
      mobile_phone?: string;
    };
  };
  onClose: () => void;
} & React.HTMLProps<HTMLDivElement>) => {
  return (
    <Modal
      opened
      onClose={onClose}
      size="lg"
      centered
      scrollAreaComponent={ScrollArea.Autosize}
      title={
        <Group gap="sm" wrap="nowrap">
          <ThemeIcon size="lg" radius="md">
            <User size={20} />
          </ThemeIcon>
          <Stack gap={0}>
            <Text fw={700} size="lg">
              Employee Details
            </Text>
            <Text size="sm" c="dimmed">
              Complete employee information
            </Text>
          </Stack>
        </Group>
      }
    >
      <Stack gap="md">
        <Section title="Basic Information">
          <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
            <Field icon={<CircleUser size={16} />} color="blue" label="Name">
              <Text span inherit fw={600}>
                {employee?.name}
              </Text>
            </Field>
            <Field icon={<Mail size={16} />} color="blue" label="Email">
              {employee?.work_email}
            </Field>
            <Field icon={<Phone size={16} />} color="blue" label="Phone">
              {employee?.mobile_phone}
            </Field>
            <Stack gap={4}>
              <Text size="xs" c="dimmed" fw={500}>
                Status
              </Text>
              <Badge
                variant="light"
                color={employee?.status ? 'green' : 'red'}
                w="fit-content"
              >
                {employee?.status ? 'Active' : 'Inactive'}
              </Badge>
            </Stack>
          </SimpleGrid>
        </Section>

        <Section title="Organization">
          <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
            <Field icon={<Building2 size={16} />} color="grape" label="Company">
              <Text span inherit fw={600}>
                {employee?.company?.name}
              </Text>
            </Field>
            <Field icon={<Folder size={16} />} color="grape" label="Project">
              <Text span inherit fw={600}>
                {employee?.project?.name}
              </Text>
            </Field>
          </SimpleGrid>
        </Section>

        <Section title="Territories">
          {employee?.territories?.length > 0 ? (
            <Group gap="xs">
              {employee.territories.map((territory: any) => (
                <Badge
                  key={territory.id}
                  variant="light"
                  color="green"
                  size="lg"
                  tt="none"
                  leftSection={<MapPin size={14} />}
                >
                  {territory.name}
                </Badge>
              ))}
            </Group>
          ) : (
            <Text size="sm" c="dimmed">
              No territories assigned
            </Text>
          )}
        </Section>

        <Section title="Warehouses">
          {employee?.warehouses?.length > 0 ? (
            <Group gap="xs">
              {employee.warehouses.map((warehouse: any) => (
                <Badge
                  key={warehouse.id}
                  variant="light"
                  color="orange"
                  size="lg"
                  tt="none"
                  leftSection={<Building2 size={14} />}
                >
                  {warehouse.name}
                </Badge>
              ))}
            </Group>
          ) : (
            <Text size="sm" c="dimmed">
              No warehouses assigned
            </Text>
          )}
        </Section>

        {(employee?.vehicle || employee?.meter_reading) && (
          <Section title="Vehicle Information">
            <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
              {employee?.vehicle && (
                <Field icon={<Truck size={16} />} color="cyan" label="Vehicle">
                  <Text span inherit fw={600}>
                    {employee.vehicle.name}
                  </Text>
                </Field>
              )}
              {employee?.meter_reading && (
                <Field
                  icon={<Gauge size={16} />}
                  color="cyan"
                  label="Meter Reading"
                >
                  <Text span inherit fw={600}>
                    {employee.meter_reading}
                  </Text>
                </Field>
              )}
            </SimpleGrid>
          </Section>
        )}

        <Section title="Reporting Manager">
          {employee?.manager ? (
            <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
              <Field icon={<CircleUser size={16} />} color="red" label="Name">
                <Text span inherit fw={600}>
                  {employee.manager.name}
                </Text>
              </Field>
              {employee.manager.work_email &&
                employee.manager.work_email !== 'false' && (
                  <Field icon={<Mail size={16} />} color="red" label="Email">
                    {employee.manager.work_email}
                  </Field>
                )}
              {employee.manager.mobile_phone && (
                <Field icon={<Phone size={16} />} color="red" label="Phone">
                  {employee.manager.mobile_phone}
                </Field>
              )}
            </SimpleGrid>
          ) : (
            <Text size="sm" c="dimmed">
              No manager assigned
            </Text>
          )}
        </Section>

        <Group justify="flex-end">
          <Button onClick={onClose}>Close</Button>
        </Group>
      </Stack>
    </Modal>
  );
};

export default EmployeeInfoModal;
